import type { CallRecord } from '@/features/calls/types'

/**
 * What a call amounted to for reaching the client:
 *   pending    — not finished yet
 *   unanswered — rang out, was busy, or went to voicemail
 *   failed     — the call went wrong, or reached someone who is not the client
 *   connected  — the client picked up
 *   unknown    — ended, but nothing says which of the above
 *
 * Only `connected` counts as contact, and only `connected` ends a run of
 * unanswered calls. A call is connected on positive evidence, never by default.
 */
export type CallReach =
  | 'pending'
  | 'unanswered'
  | 'failed'
  | 'connected'
  | 'unknown'

/**
 * The default duty label set (hoku/duty_templates/label_mapping.md), keyed by
 * what each label says about reaching the client. Matched exactly, after
 * normalizing case and separators ("call error" → "call-error"): several
 * labels for answered calls contain words like "failed" or "error"
 * (`identity-verification-failed`, `transfer-error`), so substring matching
 * would misread them.
 */
const LABEL_REACH: Record<string, CallReach> = {
  'no-answer': 'unanswered',
  busy: 'unanswered',
  'voicemail-complete': 'unanswered',
  'voicemail-incomplete': 'unanswered',
  'call-error': 'failed',
  'wrong-number': 'failed',
  'callback-request': 'connected',
  'identity-verification-failed': 'connected',
  'do-not-call': 'connected',
  'incomplete-call-customer-hung-up': 'connected',
  'incomplete-call': 'connected',
  'transfer-error': 'connected',
  other: 'connected',
}

/** Vapi ended reasons that mean nobody picked up. */
const UNANSWERED_ENDED_REASONS = new Set([
  'customer-did-not-answer',
  'customer-busy',
  'voicemail',
])

/**
 * Vapi ended reasons that mean the call itself went wrong: the pipeline-error-*,
 * *-failed-to-connect-call, assistant-error and call.start.error families.
 */
const FAILED_ENDED_REASON = /error|failed/

/** Vapi ended reasons that mean a conversation happened. */
const CONNECTED_ENDED_REASONS = new Set([
  'customer-ended-call',
  'assistant-ended-call',
  'assistant-said-end-call-phrase',
  'assistant-forwarded-call',
  'assistant-ended-call-after-message-spoken',
  'assistant-ended-call-with-hangup-task',
  'exceeded-max-duration',
])

/**
 * Duties can write their own labels. One outside the default set that still
 * says voicemail, no answer or busy is read that way; anything else is left
 * to the ended reason.
 */
const UNANSWERED_CUSTOM_LABEL = /voice\s*mail|\bvm\b|no[\s-]answer|\bbusy\b/i

function normalizedLabel(label: string | null | undefined): string {
  return (label ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
}

export function callReach(call: CallRecord): CallReach {
  const endedReason = call.ended_reason?.trim().toLowerCase() ?? ''
  if (!endedReason && call.status?.toLowerCase() !== 'ended') return 'pending'

  const label = normalizedLabel(call.label)
  const fromLabel = LABEL_REACH[label]
  if (fromLabel) return fromLabel

  if (UNANSWERED_ENDED_REASONS.has(endedReason)) return 'unanswered'
  if (FAILED_ENDED_REASON.test(endedReason)) return 'failed'
  if (UNANSWERED_CUSTOM_LABEL.test(call.label ?? '')) return 'unanswered'
  if (CONNECTED_ENDED_REASONS.has(endedReason)) return 'connected'
  return 'unknown'
}

/** How a call reads in a sentence: "voicemail", "busy", "no answer"... */
export function callReachPhrase(call: CallRecord): string {
  const text = `${call.ended_reason ?? ''} ${call.label ?? ''}`
  switch (callReach(call)) {
    case 'pending':
      return 'in progress'
    case 'unanswered':
      if (/voice\s*mail|\bvm\b/i.test(text)) return 'voicemail'
      if (/busy/i.test(text)) return 'busy'
      return 'no answer'
    case 'failed':
      return normalizedLabel(call.label) === 'wrong-number'
        ? 'wrong number'
        : 'call error'
    case 'connected':
      return 'connected'
    case 'unknown':
      return 'outcome unknown'
  }
}
